import {
  Instruction,
  InstructionWithButtonGroup,
  InstructionWithContent,
  InstructionWithData,
  ProgramContentType,
  Hidden,
} from "../../BlockComponent";
import {
  AudioPlayer,
  DateSelect,
  LineWithSolidLabel,
  Percentage,
  TextareaLine,
} from "../../CellComponent";
import dayjs from "dayjs";
import { CardMeditation } from "../common";
import { MeditationGroupType } from "../../../api/meditationRecordApi";

const today = dayjs().format("YYYY-MM-DD");

export type MeditationType =
  | "grounding"
  | "relaxation"
  | "breath"
  | "willingness"
  | "routine";

export const MeditationTemplate_new = ({
  taskKey,
  groupType = "routine",
  includingMeditation,
  mrk,
  isFromThoughtRecord,
  isSituationUpgraded,
}: {
  taskKey: string;
  groupType?: MeditationGroupType;
  includingMeditation: MeditationType[];
  mrk?: string;
  isFromThoughtRecord?: boolean;
  isSituationUpgraded?: boolean;
}): ProgramContentType[] => {
  const isWillingnessAvailable = includingMeditation.includes("willingness");
  return [
    Hidden([
      mrk
        ? [
            {
              type: "temp",
              content: {
                id: `meditationRecordKey`,
                value: mrk,
              },
            },
          ]
        : [],
      [
        {
          type: "temp",
          content: {
            id: `date`,
            value: today,
          },
        },
      ],
      [
        {
          type: "temp",
          content: {
            id: `groupType`,
            value: groupType || "routine",
          },
        },
      ],
    ]),
    ...(groupType === "coping"
      ? [
          InstructionWithButtonGroup(
            "coping_0",
            [
              {
                text: "감정 - 심호흡 그라운딩",
                showFromId: "grounding",
                hideFromId: "relaxation",
              },
              {
                text: "신체 반응 - 근육이완법",
                showFromId: "relaxation",
                hideFromId: "breath",
              },
              ...(includingMeditation.includes("breath")
                ? [
                    {
                      text: "생각 - 호흡 명상",
                      showFromId: "breath",
                      hideFromId: "willingness",
                    },
                  ]
                : []),
              ...(includingMeditation.includes("willingness")
                ? [
                    {
                      text: "심하진 않음 - 기꺼이 경험하기",
                      showFromId: "willingness",
                      hideFromId: "grounding",
                    },
                  ]
                : []),
            ],
            { id: "coping" },
            { blockId: "coping" }
          ),
        ]
      : [
          InstructionWithButtonGroup(
            `routine_0`,
            [
              {
                text: "5분",
                showFromId: `5min`,
                hideFromId: `10min`,
              },
              {
                text: "10분",
                showFromId: `10min`,
                hideFromId: `5min`,
              },
              {
                text: "15분",
                showFromId: `15min`,
                hideFromId: `5min`,
              },
              {
                text: "20분",
                showFromId: `20min`,
                hideFromId: `5min`,
              },
            ],
            undefined,
            { blockId: "routine" }
          ),
        ]),

    ...(groupType === "coping"
      ? [
          { blockId: "grounding", audioUrl: `grounding` },
          { blockId: "relaxation", audioUrl: `relaxation` },
          { blockId: "breath", audioUrl: `breath` },
          { blockId: "willingness", audioUrl: `willingness` },
        ]
          .map(({ blockId, audioUrl }, index) => {
            return [
              Instruction(`coping_${blockId}_0`, { blockId: blockId }),
              InstructionWithContent("audio_instruction", [
                AudioPlayer(audioUrl, undefined, mrk),
              ]),
              Instruction(`coping_${blockId}_1`, {
                ...((!isWillingnessAvailable ||
                  (isWillingnessAvailable && blockId === "willingness")) && {
                  nextBlockId: isFromThoughtRecord ? "wrapup" : "ask_deeper",
                }),
              }),
              ...(isWillingnessAvailable && blockId !== "willingness"
                ? [
                    InstructionWithButtonGroup(`coping_${blockId}_2`, [
                      {
                        text: "기꺼이 경험하기 명상 훈련을 이어서 해볼게요",
                        showFromId: "willingness",
                        hideFromId: isFromThoughtRecord
                          ? "wrapup"
                          : "ask_deeper",
                      },
                      {
                        text: "괜찮아요",
                        showFromId: isFromThoughtRecord
                          ? "wrapup"
                          : "ask_deeper",
                        hideFromId: "willingness",
                      },
                    ]),
                  ]
                : []),
            ];
          })
          .flat()
      : [
          ...["5min", "10min", "15min", "20min"]
            .map((duration) => [
              InstructionWithButtonGroup(
                `routine_1`,
                [
                  {
                    text: "생각의 강",
                    showFromId: `stream_${duration}`,
                    hideFromId: `sky_${duration}`,
                  },
                  {
                    text: "생각의 하늘",
                    showFromId: `sky_${duration}`,
                    hideFromId: `stream_${duration}`,
                  },
                  {
                    text: "생각의 공장",
                    showFromId: `factory_${duration}`,
                    hideFromId: `stream_${duration}`,
                  },
                ],
                undefined,
                { blockId: duration }
              ),
              ...[
                {
                  blockId: `stream_${duration}`,
                  audioUrl: `stream_of_thoughts_continue_${duration}`,
                  extraAudioUrl: `stream_of_thoughts_loop_5min`,
                },
                {
                  blockId: `sky_${duration}`,
                  audioUrl: `sky_of_thoughts_continue_${duration}`,
                  extraAudioUrl: `sky_of_thoughts_loop_5min`,
                },
                {
                  blockId: `factory_${duration}`,
                  audioUrl: `factory_of_thoughts_continue_${duration}`,
                  extraAudioUrl: `factory_of_thoughts_loop_5min`,
                },
              ].map(({ blockId, audioUrl, extraAudioUrl }) => {
                return InstructionWithContent(
                  "audio_instruction",
                  [AudioPlayer(audioUrl, extraAudioUrl, mrk)],
                  { blockId: blockId, nextBlockId: "routine_wrapup" }
                );
              }),
            ])
            .flat(),
          InstructionWithData({ A: { id: "meditation_time" } }, "routine_2", {
            blockId: "routine_wrapup",
          }),
          Instruction("routine_3"),
          Instruction("routine_4"),
        ]),

    ...(!isFromThoughtRecord && groupType === "coping"
      ? [
          InstructionWithButtonGroup(
            `coping_1`,
            [
              {
                text: "네",
                showFromId: "thoughtRecord_start",
                hideFromId: "wrapup",
              },
              {
                text: "아니요",
                showFromId: "wrapup",
                hideFromId: "thoughtRecord_start",
              },
            ],
            undefined,
            { blockId: "ask_deeper" }
          ),

          InstructionWithContent(
            `thoughtRecord_impact`,
            [
              ...LineWithSolidLabel("영향 정도", [
                Percentage({
                  id: `percentageThought`,
                }),
              ]),
            ],
            { blockId: "thoughtRecord_start" }
          ),
          InstructionWithContent(
            `thoughtRecord_date`,
            LineWithSolidLabel("날짜", [
              DateSelect({ id: "date", value: today }),
            ])
          ),

          InstructionWithContent(
            `thoughtRecord_situation`,
            isSituationUpgraded
              ? [
                  ...LineWithSolidLabel(
                    "트리거",
                    TextareaLine({
                      id: "situation_direct",
                      placeholder: "직접적인 상황",
                    })
                  ),
                  ...LineWithSolidLabel(
                    "맥락",
                    TextareaLine({
                      id: "situation_indirect",
                      placeholder: "간접적인 상황",
                    })
                  ),
                ]
              : [
                  ...LineWithSolidLabel(
                    "상황",
                    TextareaLine({ id: "situation" })
                  ),
                ]
          ),
          Instruction("coping_3", { nextBlockId: "wrapup" }),
        ]
      : []),

    ...CardMeditation,
    Instruction("wrapup_0", { blockId: "wrapup" }),
    Instruction("wrapup_1"),
  ];
};
